import { Form, Popconfirm, Table, Tooltip, notification, Drawer } from "antd";
import { ColumnsType } from "antd/es/table";
import React, { useEffect, useState } from "react";
import { Button, Card, Col, Row } from "react-bootstrap";
import { Icon } from "ts-react-feather-icons";
import { DragDropContext, Droppable, Draggable, DropResult } from "react-beautiful-dnd";
import { ActionType } from "../../constants/actionType";
import { UserRole } from "../../constants/userRole";
import { deleteEpisodeIntro, getEpisodeIntros, reorderEpisodeIntros } from "../../helpers/api/story";
import { useUser } from "../../hooks";
import User from "../../model/User";
import { EpisodeIntroModel } from "../../model/EpisodeIntro";
import EpisodeIntroDrawer from "./EpisodeIntroDrawer";
import VideoPlayer from "../VideoPlayer";
import ImageViewer from "../ImageViewer";
import "./EpisodeIntroTable.scss";

const EpisodeIntroTable = ({
    episode,
    drawerVisibility,
    setDrawerVisibility
}: any) => {
    const [intros, setIntros] = useState<EpisodeIntroModel[]>([]);
    const [originalIntros, setOriginalIntros] = useState<EpisodeIntroModel[]>([]);
    const [introDetail, setIntroDetail] = useState<EpisodeIntroModel | null>(null);
    const [actionType, setActionType] = useState<string>("");
    const [introDrawerVisibility, setIntroDrawerVisibility] = useState<boolean>(false);
    const [searchLoading, setSearchLoading] = useState<boolean>(false);
    const [reorderLoading, setReorderLoading] = useState<boolean>(false);
    const [hasOrderChanged, setHasOrderChanged] = useState<boolean>(false);

    const currentLoggedInUser = useUser().user as User || null;
    const [api, contextHolder] = notification.useNotification();

    const showError = (errorMessage: string) => {
        api.error({
            message: `Error notification`,
            description: <span className="text-danger">{errorMessage || 'There\'re some errors occurred!'}</span>,
            placement: 'topRight'
        });
    }

    useEffect(() => {
        if (!episode) {
            return;
        }
        search();
    }, [episode]);

    const search = async () => {
        setSearchLoading(true);
        try {
            const response = await getEpisodeIntros(episode.id);
            if (response.status === 200) {
                setIntros(response.data.data || []);
                setOriginalIntros(response.data.data || []);
            }
        } catch (err) {
            console.error(err);
            showError(err as string);
        }
        setSearchLoading(false);
    }

    const triggerUpdate = (intro: EpisodeIntroModel) => {
        setIntroDetail(intro);
        setActionType(ActionType.UPDATE);
        setIntroDrawerVisibility(true);
    }

    const triggerDelete = async (intro: EpisodeIntroModel) => {
        try {
            if (!intro || !intro?.id) {
                return;
            }
            await deleteEpisodeIntro(episode.id, intro.id);
            api.success({
                message: `Success notification`,
                description: 'Delete intro successfully!',
                placement: 'topRight'
            });
            search();
        } catch (err) {
            console.error(err);
            showError(err as string);
        }
    }

    const handleDragEnd = async (result: DropResult) => {
        if (!result.destination) {
            return;
        }

        const items = Array.from(intros);
        const [reorderedItem] = items.splice(result.source.index, 1);
        items.splice(result.destination.index, 0, reorderedItem);

        // Update index values based on new order
        const updatedItems = items.map((item, index) => ({
            ...item,
            index: index + 1 // Start from 1
        }));

        setIntros(updatedItems);
        setHasOrderChanged(true);
    }

    const saveOrder = async () => {
        setReorderLoading(true);
        try {
            const introIds = intros.map(item => item.id);
            console.log('Saving reorder with data:', { introIds, episodeId: episode.id });
            
            await reorderEpisodeIntros(episode.id, introIds);
            api.success({
                message: `Success notification`,
                description: 'Save order successfully!',
                placement: 'topRight'
            });
            setOriginalIntros([...intros]);
            setHasOrderChanged(false);
        } catch (err) {
            console.error('Save order error:', err);
            showError(err as string);
            // Revert to original order if API fails
            setIntros([...originalIntros]);
            setHasOrderChanged(false);
        }
        setReorderLoading(false);
    }

    const cancelOrderChanges = () => {
        setIntros([...originalIntros]);
        setHasOrderChanged(false);
    }

    const onClose = () => {
        setDrawerVisibility(false);
        setIntros([]);
        setOriginalIntros([]);
        setIntroDetail(null);
        setActionType("");
        setHasOrderChanged(false);
    }

    const renderMedia = (record: EpisodeIntroModel) => {
        if (!record.mediaUrl) {
            return <span className="text-muted">No media</span>;
        }
        
        if (record.type === 'VIDEO') {
            return (
                <VideoPlayer 
                    url={record.mediaUrl}
                    width={60}
                    height={40}
                />
            );
        } else {
            return (
                <ImageViewer 
                    url={record.mediaUrl}
                    width={60}
                    height={40}
                    alt="intro media"
                />
            );
        }
    }

    const renderText = (record: EpisodeIntroModel) => {
        return record.text ? (
            <span style={{ maxWidth: "200px", display: "block" }}>
                {record.text.length > 50 ? `${record.text.substring(0, 50)}...` : record.text}
            </span>
        ) : (
            <span className="text-muted">No text</span>
        )
    }

    const renderActions = (record: EpisodeIntroModel) => {
        return (
            (currentLoggedInUser?.roles?.includes(UserRole.SUPER_ADMIN) || currentLoggedInUser?.roles?.includes(UserRole.ADMIN)) && (
                <>
                    <Tooltip title="Update">
                        <span role="button"
                            onClick={() => triggerUpdate(record)}
                            className="d-inline-block me-2"
                        >
                            <Icon name="edit-2" color="#0d6efd" size={16} />
                        </span>
                    </Tooltip>
                    <Tooltip title="Delete">
                        <Popconfirm
                            title={`Delete intro`}
                            description={`Are you sure to delete this intro?`}
                            onConfirm={() => triggerDelete(record)}
                            okText="OK"
                            cancelText="Cancel"
                        >
                            <span role="button" className="d-inline-block">
                                <Icon name="trash-2" color="#dc3545" size={16} />
                            </span>
                        </Popconfirm>
                    </Tooltip>
                </>
            )
        );
    }

    return (
        <>
            {contextHolder}

            <EpisodeIntroDrawer
                episode={episode}
                drawerVisibility={introDrawerVisibility}
                setdDrawerVisibility={setIntroDrawerVisibility}
                introDetail={introDetail}
                setIntroDetail={setIntroDetail}
                actionType={actionType}
                setActionType={setActionType}
                existingIntros={intros}
                onSuccess={() => {
                    api.success({
                        message: `Success notification`,
                        description: (actionType === ActionType.CREATE ? "Create" : "Update") + ' intro successfully!',
                        placement: 'topRight'
                    });
                    setIntroDetail(null);
                    setActionType("");
                    search();
                }}
                onFailure={(errorMessage: string) => {
                    showError(errorMessage);
                }}
            />

            <Drawer
                title={`Episode Intros - ${episode?.name || ''}`}
                className='episode-intro-table-drawer'
                width={1200}
                onClose={onClose}
                open={drawerVisibility}
                closeIcon={true}
            >
                <Row>
                    <Col>
                        <Card>
                            <Card.Body>
                                <Row>
                                    <Col sm={6} className="mb-2">
                                        <span className="fw-bold lead text-info">EPISODE INTROS</span>
                                        {hasOrderChanged && (
                                            <span className="ms-2 badge bg-warning">
                                                <i className="mdi mdi-alert-circle me-1"></i>
                                                Order changed
                                            </span>
                                        )}
                                        {reorderLoading && (
                                            <span className="ms-2 badge bg-info">
                                                <i className="mdi mdi-sync mdi-spin me-1"></i>
                                                Saving...
                                            </span>
                                        )}
                                    </Col>
                                    <Col sm={6} className="mb-2">
                                        <div className="text-sm-end">
                                            {hasOrderChanged && (
                                                <>
                                                    <Button 
                                                        variant="outline-secondary" 
                                                        className="mb-1 py-1 me-1" 
                                                        onClick={cancelOrderChanges}
                                                        disabled={reorderLoading}
                                                    >
                                                        <i className="mdi mdi-close me-1"></i> Cancel
                                                    </Button>
                                                    <Button 
                                                        variant="success" 
                                                        className="mb-1 py-1 me-1" 
                                                        onClick={saveOrder}
                                                        disabled={reorderLoading}
                                                    >
                                                        <i className="mdi mdi-content-save me-1"></i> Save Order
                                                    </Button>
                                                </>
                                            )}
                                            <Button variant="info" className="mb-1 py-1 me-1" onClick={() => {
                                                search();
                                            }}>
                                                <i className="mdi mdi-refresh me-1"></i> Refresh
                                            </Button>
                                            <Button variant="primary" className="mb-1 py-1 me-1" onClick={() => {
                                                setActionType(ActionType.CREATE);
                                                setIntroDrawerVisibility(true);
                                            }}>
                                                <i className="mdi mdi-plus-circle me-1"></i> Create Intro
                                            </Button>
                                        </div>
                                    </Col>
                                </Row>

                                {(searchLoading || reorderLoading) && (
                                    <div className="text-center py-4">
                                        <div className="spinner-border text-primary" role="status">
                                            <span className="visually-hidden">Loading...</span>
                                        </div>
                                    </div>
                                )}

                                {!searchLoading && !reorderLoading && (
                                    <DragDropContext onDragEnd={handleDragEnd}>
                                        <Droppable droppableId="intros">
                                            {(provided) => (
                                                <div
                                                    {...provided.droppableProps}
                                                    ref={provided.innerRef}
                                                    className="custom-table-container"
                                                >
                                                    <table className="table table-hover m-0 table-centered dt-responsive nowrap w-100">
                                                        <thead>
                                                            <tr>
                                                                <th style={{ width: '60px' }}>Drag</th>
                                                                <th style={{ width: '80px' }}>Order</th>
                                                                <th style={{ width: '120px' }}>Type</th>
                                                                <th style={{ width: '120px' }}>Media</th>
                                                                <th>Text</th>
                                                                <th style={{ width: '120px' }}>Created by</th>
                                                                <th style={{ width: '150px' }}>Created at</th>
                                                                <th style={{ width: '120px' }}>Action</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {intros.map((intro, index) => (
                                                                <Draggable
                                                                    key={intro.id}
                                                                    draggableId={intro.id}
                                                                    index={index}
                                                                >
                                                                    {(provided, snapshot) => (
                                                                        <tr
                                                                            ref={provided.innerRef}
                                                                            {...provided.draggableProps}
                                                                            className={snapshot.isDragging ? 'dragging' : ''}
                                                                            style={{
                                                                                ...provided.draggableProps.style,
                                                                                backgroundColor: snapshot.isDragging ? '#f0f0f0' : 'transparent',
                                                                                opacity: reorderLoading ? 0.6 : 1
                                                                            }}
                                                                        >
                                                                            <td>
                                                                                <div 
                                                                                    {...provided.dragHandleProps}
                                                                                    className="drag-handle"
                                                                                    style={{ 
                                                                                        cursor: reorderLoading ? 'not-allowed' : 'grab',
                                                                                        opacity: reorderLoading ? 0.5 : 1
                                                                                    }}
                                                                                >
                                                                                    <Icon name="menu" size={16} color="#666" />
                                                                                </div>
                                                                            </td>
                                                                            <td>
                                                                                <span className="badge bg-secondary">
                                                                                    {intro.index}
                                                                                </span>
                                                                            </td>
                                                                            <td>
                                                                                <span className={`badge ${intro.type === 'VIDEO' ? 'bg-primary' : 'bg-success'}`}>
                                                                                    {intro.type === 'VIDEO' ? 'Video' : 'Image with Text'}
                                                                                </span>
                                                                            </td>
                                                                            <td>{renderMedia(intro)}</td>
                                                                            <td>{renderText(intro)}</td>
                                                                            <td>{intro.createdBy}</td>
                                                                            <td>{intro?.createdAt ? new Date(intro?.createdAt).toLocaleString() : ''}</td>
                                                                            <td>{renderActions(intro)}</td>
                                                                        </tr>
                                                                    )}
                                                                </Draggable>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                    {provided.placeholder}
                                                </div>
                                            )}
                                        </Droppable>
                                    </DragDropContext>
                                )}

                                {intros.length === 0 && !searchLoading && !reorderLoading && (
                                    <div className="text-center py-4">
                                        <p className="text-muted">No intros found for this episode.</p>
                                    </div>
                                )}
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            </Drawer>
        </>
    )
}

export default EpisodeIntroTable; 